import React, { createContext, useState, useEffect, useContext } from 'react';
import { trackEvent } from '../utils/analytics';
import { getAttribution } from '../utils/attribution';
import toast from 'react-hot-toast';
import { getMe, loginUser, registerUser, verifyLoginOtp, getLinkedRoles, switchAccountRole } from '../utils/api';
import { SESSION_ENDED_EVENT } from '../utils/tokenRefresh';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkedRoles, setLinkedRoles] = useState([]);

    const refreshLinkedRoles = async () => {
        try {
            const res = await getLinkedRoles();
            setLinkedRoles(res.data.roles || []);
        } catch (error) {
            setLinkedRoles([]);
        }
    };

    // The matrimonial member's religion drives the logged-in site palette.
    // Hindu → deep maroon, Christian → sky blue, Muslim → green; nobody logged in → 'guest' (default palette).
    useEffect(() => {
        if (!user && localStorage.getItem('token')) return;
        if (!user) {
            document.documentElement.dataset.religion = 'guest';
            return;
        }
        const value = String(user.religion || 'Hindu').toLowerCase();
        const religion = value === 'christian' || value === 'muslim' ? value : 'hindu';
        document.documentElement.dataset.religion = religion;
        localStorage.setItem('gettimelam_religion_theme', religion);
    }, [user?.religion, !!user]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            getMe()
                .then(res => { setUser(res.data.user); return refreshLinkedRoles(); })
                .catch(() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('gettimelam_religion_theme');
                    document.documentElement.dataset.religion = 'guest';
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    // A session older than the server allows (30 days idle) cannot be refreshed: sign out cleanly with one clear message.
    useEffect(() => {
        const sessionEnded = () => {
            localStorage.removeItem('gettimelam_religion_theme');
            document.documentElement.dataset.religion = 'guest';
            setUser(null);
            setLinkedRoles([]);
            toast('Your session has ended. Please log in again.', { id: 'session-ended' });
        };
        window.addEventListener(SESSION_ENDED_EVENT, sessionEnded);
        return () => window.removeEventListener(SESSION_ENDED_EVENT, sessionEnded);
    }, []);

    // ✅ ADDED — re-fetch user when tab becomes visible (picks up admin verification instantly)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
                getMe()
                    .then(res => setUser(res.data.user))
                    .catch(() => { });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const login = async (data) => {
        const res = await loginUser(data);
        localStorage.setItem('token', res.data.token);
        const current = await getMe();
        setUser(current.data.user);
        await refreshLinkedRoles();
        trackEvent('login', { method: 'password', role: current.data.user?.role || '' });
        return { ...res.data, user: current.data.user };
    };

    const register = async (data) => {
        const res = await registerUser({ ...data, acquisition: getAttribution() });
        localStorage.setItem('token', res.data.token);
        const current = await getMe();
        setUser(current.data.user);
        await refreshLinkedRoles();
        trackEvent('sign_up', { method: 'form', role: current.data.user?.role || '' });
        return { ...res.data, user: current.data.user };
    };

    const loginWithOtp = async (data) => {
        const res = await verifyLoginOtp(data);
        localStorage.setItem('token', res.data.token);
        const current = await getMe();
        setUser(current.data.user);
        await refreshLinkedRoles();
        trackEvent('login', { method: 'otp', role: current.data.user?.role || '' });
        return { ...res.data, user: current.data.user };
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('gettimelam_religion_theme');
        document.documentElement.dataset.religion = 'guest';
        setUser(null);
        setLinkedRoles([]);
    };

    // ✅ Refresh user data from server (useful when admin changes premium status)
    const refreshUser = async () => {
        try {
            const res = await getMe();
            setUser(res.data.user);
        } catch (err) {
            console.log('Failed to refresh user');
        }
    };

    const switchRole = async (role) => {
        const res = await switchAccountRole(role);
        localStorage.setItem('token', res.data.token);
        const current = await getMe();
        setUser(current.data.user);
        await refreshLinkedRoles();
        return current.data.user;
    };

    return (
        <AuthContext.Provider value={{ user, loading, linkedRoles, login, register, loginWithOtp, logout, refreshUser, switchRole }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
